import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridToolbarContainer,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { Manager } from "../../types/users";
import { useNavigate } from "react-router-dom";
import {
  useGetManagersQuery,
  useDeleteManagerMutation,
  useRestoreManagerMutation,
} from "../../services/manager.service";
import ModalManagerForm from "./ModalManagerForm";
import { CardBase, GlassDataGrid, StyledTextField } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

const TableToolbar = ({
  onSearch,
  onCreate,
}: {
  onSearch: (query?: string) => void;
  onCreate: () => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const enterToSearchLabel = useUILabel("common.enterToSearch");
  const resetLabel = useUILabel("common.reset");
  const addManagerLabel = useUILabel("manager.addManager");
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    onSearch(debounceQuery);
  }, [debounceQuery, onSearch]);

  return (
    <GridToolbarContainer
      sx={{
        padding: { xs: "12px 16px", sm: "16px 20px" },
        marginBottom: 0,
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        gap: { xs: 1.5, sm: 2 },
      }}
    >
      <Box flex={1} sx={{ display: "flex", alignItems: "center", gap: 1, width: { xs: "100%", sm: "auto" } }}>
        <StyledTextField
          margin="dense"
          label={enterToSearchLabel}
          value={query || ""}
          onChange={(e) => setQuery(e.target.value)}
          sx={{
            minWidth: { xs: "100%", sm: 250 },
            flex: { xs: 1, sm: "none" },
          }}
        />
        {!!query && (
          <Button
            variant="text"
            sx={{
              mt: { xs: 0, sm: 1 },
              whiteSpace: "nowrap",
            }}
            onClick={() => setQuery("")}
          >
            {resetLabel}
          </Button>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          flexShrink: 0,
          width: { xs: "100%", sm: "auto" },
          justifyContent: { xs: "flex-start", sm: "flex-end" },
        }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={onCreate}
          startIcon={<AddIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {addManagerLabel}
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
};

const ManagersPage = () => {
  const workManagersLabel = useUILabel("manager.workManagers");
  const nameLabel = useUILabel("common.name");
  const emailLabel = useUILabel("common.email");
  const phoneNumberLabel = useUILabel("common.phoneNumber");
  const editDeleteLabel = useUILabel("common.editDelete");
  const restoreLabel = useUILabel("common.restore");
  const successfullyDeletedLabel = useUILabel("common.successfullyDeleted");
  const deleteManagerQuestionLabel = useUILabel("manager.deleteManagerQuestion");
  const dataRestoredLabel = useUILabel("manager.dataRestored");
  const nALabel = useUILabel("manager.nA");

  useTitle(workManagersLabel);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const addManagerLabel = useUILabel("manager.addManager");

  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: GridRowModel<Manager> | null;
  }>({
    open: false,
    row: null,
  });
  const [formModal, setFormModal] = React.useState<{
    open: boolean;
    data: Manager | null;
  }>({
    open: false,
    data: null,
  });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );

  const { data, isLoading, isFetching } = useGetManagersQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doDelete, { isLoading: deleting }] = useDeleteManagerMutation();
  const [doRestore, { isLoading: restoring }] = useRestoreManagerMutation();

  const handleDeleteRow = (row: GridRowModel<Manager>) => {
    setDeleteModal({ open: true, row });
  };
  const handleEditRow = (row: GridRowModel<Manager>) => {
    setFormModal({ open: true, data: row });
  };
  const handleRestoreRow = async (row: GridRowModel<Manager>) => {
    try {
      await doRestore(row.id).unwrap();
      toast.success(dataRestoredLabel);
    } catch (err) { }
  };

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "first_name",
        headerName: nameLabel,
        flex: 1,
        minWidth: isMobile ? 150 : 200,
        sortingOrder: ["desc", "asc", null],
        filterable: true,
        valueGetter: (params: GridValueGetterParams) => {
          if (!params.row) return "";
          return `${params.row.first_name || ""} ${params.row.last_name || ""}`.trim() || nALabel;
        },
        renderCell: (params: GridRenderCellParams) => {
          if (!params.row) {
            return <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>{nALabel}</Typography>;
          }
          const firstName = params.row.first_name || "";
          const lastName = params.row.last_name || "";
          const fullName = `${firstName} ${lastName}`.trim();
          return (
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: "0.75rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
              {fullName || nALabel}
            </Typography>
          );
        },
      },
      {
        field: "email",
        headerName: emailLabel,
        headerAlign: "center",
        minWidth: isMobile ? 150 : 200,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, wordBreak: "break-word" }}>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "phone_number",
        headerName: phoneNumberLabel,
        headerAlign: "center",
        minWidth: isMobile ? 120 : 200,
        sortable: true,
        filterable: true,
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "control",
        headerName: editDeleteLabel,
        headerAlign: "center",
        minWidth: isMobile ? 100 : 130,
        sortable: false,
        filterable: false,
        align: "center",
        renderCell: (params: GridRenderCellParams) =>
          !!params.row.deleted_at ? (
            <Button
              variant="text"
              color="secondary"
              onClick={() => handleRestoreRow(params.row)}
              size={isMobile ? "small" : "medium"}
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.875rem" },
                px: { xs: 1, sm: 2 },
              }}
            >
              {restoreLabel}
            </Button>
          ) : (
            <Stack direction={"row"}>
              <IconButton onClick={() => handleEditRow(params.row)} size={isMobile ? "small" : "medium"}>
                <EditIcon fontSize={isMobile ? "small" : "small"} />
              </IconButton>
              <IconButton onClick={() => handleDeleteRow(params.row)} size={isMobile ? "small" : "medium"}>
                <DeleteIcon fontSize={isMobile ? "small" : "small"} />
              </IconButton>
            </Stack>
          ),
      },
    ],
    [isMobile, nameLabel, emailLabel, phoneNumberLabel, editDeleteLabel, restoreLabel, handleEditRow, handleDeleteRow, handleRestoreRow, nALabel]
  );

  const handleSearch = React.useCallback((query?: string) => {
    setSearchQuery(query);
  }, []);

  const handleCreate = React.useCallback(() => {
    setFormModal({ open: true, data: null });
  }, []);

  const closeDeleteModal = () =>
    setDeleteModal({ ...deleteModal, open: false });

  const handleDelete = async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete(deleteModal.row.id).unwrap();
        closeDeleteModal();
        toast.success(successfullyDeletedLabel);
      } catch (err) {
        closeDeleteModal();
      }
    }
  };

  const CustomToolbar = React.useMemo(
    () => () =>
      <TableToolbar onSearch={handleSearch} onCreate={handleCreate} />,
    [handleSearch, handleCreate]
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <Box sx={{ width: "auto", px: { xs: 0, sm: 3 }, py: 3 }}>
      <CardBase
        title={workManagersLabel}
        useGlassCard={true}
        nested={false}
        sx={{
          "& .card-base__header": {
            flexDirection: { xs: "row", sm: "row" },
            alignItems: { xs: "center", sm: "center" },
            gap: { xs: 1.5, sm: 2 },
            flexWrap: { xs: "wrap", sm: "nowrap" },
          },
          "& .card-base__header > .MuiBox-root:first-of-type": { flex: 1 },
        }}
        headerActions={
          <Button
            variant="contained"
            color="success"
            onClick={handleCreate}
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              width: { xs: "auto", sm: "auto" },
              whiteSpace: "nowrap",
            }}
          >
            {addManagerLabel}
          </Button>
        }
      >
        <GlassDataGrid
          height={isMobile ? "75vh" : "85vh"}
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page as any}
          onPageChange={(newPage: any) => setPage(newPage)}
          pageSize={pageSize as any}
          rowsPerPageOptions={[100]}
          onPageSizeChange={(newSize: any) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: LinearProgress,
            NoResultsOverlay: NoResultsOverlay,
            Toolbar: CustomToolbar,
          }}
          disableSelectionOnClick
          disableColumnFilter
          density="compact"
          getRowClassName={getRowClassName}
          rowHeight={isMobile ? 56 : 64}
          sx={{
            "& .MuiDataGrid-toolbarContainer": {
              padding: { xs: "12px 16px", sm: "16px 20px" },
              backgroundColor: "transparent",
              borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(15px) saturate(180%)",
              WebkitBackdropFilter: "blur(15px) saturate(180%)",
              borderBottom: `2px solid ${isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(46, 125, 60, 0.3)"}`,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: { xs: "0.7rem", sm: "0.875rem" },
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#12903C",
              },
            },
            "& .MuiDataGrid-cell": {
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            },
            "& .deleted": {
              fontStyle: "italic",
              color: isDark ? "rgba(255, 255, 255, 0.5)" : "#aaa",
            },
          }}
        />
      </CardBase>
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        message={deleteManagerQuestionLabel.replace("{name}", deleteModal.row?.first_name || "")}
      />
      <ModalManagerForm
        open={formModal.open}
        onClose={() => setFormModal({ open: false, data: null })}
        data={formModal.data}
      />
    </Box>
  );
};
export default ManagersPage;
